import { useState } from "react";
import { Box } from "@mui/material";
import { formatLabel } from "@/utils/format-label";
import { ActivitySheet } from "./activity-sheet";

function ViewActivityCell({ activity }) {
  const [sheetOpen, setSheetOpen] = useState(false);

  return (
    <>
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          setSheetOpen(true);
        }}
        style={{
          color: "#2563eb",
          fontWeight: 500,
          fontSize: "0.85rem",
          cursor: "pointer",
        }}
      >
        View
      </span>

      <ActivitySheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        activityId={activity?.id}
      />
    </>
  );
}

const getCategoryColor = (category) => {
  switch ((category ?? "").toLowerCase()) {
    case "authentication":
      return { bg: "#eff6ff", color: "#1e40af", dot: "#3b82f6" };
    case "transaction":
      return { bg: "#ecfdf5", color: "#065f46", dot: "#10b981" };
    case "kyc":
      return { bg: "#faf5ff", color: "#6b21a8", dot: "#a855f7" };
    case "profile":
      return { bg: "#fff7ed", color: "#9a3412", dot: "#f97316" };
    case "product":
      return { bg: "#f0f9ff", color: "#075985", dot: "#0ea5e9" };
    default:
      return { bg: "#f3f4f6", color: "#374151", dot: "#9ca3af" };
  }
};

const getStatusColor = (status) => {
  switch ((status ?? "").toLowerCase()) {
    case "success":
      return { bg: "#ecfdf5", color: "#065f46" };
    case "failed":
    case "failure":
    case "error":
      return { bg: "#fef2f2", color: "#991b1b" };
    default:
      return { bg: "#fffbeb", color: "#92400e" };
  }
};

const formatTimestamp = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";

  // occurredAt may arrive as a unix timestamp in seconds (fractional)
  // or as an ISO date string — handle both.
  const asNumber = Number(value);
  const date = Number.isNaN(asNumber)
    ? new Date(value)
    : new Date(asNumber * 1000);

  if (Number.isNaN(date.getTime())) return "N/A";

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const ActivityColumns = [
  {
    accessorKey: "occurredAt",
    header: () => <div style={{ whiteSpace: "nowrap" }}>Date & Time</div>,
    cell: ({ row }) => (
      <div
        style={{ fontSize: "0.85rem", whiteSpace: "nowrap", color: "#6b7280" }}
      >
        {formatTimestamp(row.original.occurredAt)}
      </div>
    ),
  },
  {
    accessorKey: "actorName",
    header: () => <div style={{ whiteSpace: "nowrap" }}>Actor</div>,
    cell: ({ row }) => {
      const { actorName, actorEmail, actorType } = row.original;
      return (
        <div>
          <div
            style={{ fontSize: "0.85rem", fontWeight: 500, whiteSpace: "nowrap" }}
          >
            {actorName ?? formatLabel(actorType) ?? "Anonymous"}
          </div>
          <div style={{ fontSize: "0.75rem", color: "#6b7280" }}>
            {actorEmail ?? "—"}
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "action",
    header: () => <div style={{ whiteSpace: "nowrap" }}>Activity</div>,
    cell: ({ row }) => {
      const { dot } = getCategoryColor(row.original.category);
      return (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: dot,
              flexShrink: 0,
              marginTop: 5,
            }}
          />
          <div style={{ maxWidth: 360, minWidth: 0 }}>
            <div
              style={{
                fontSize: "0.85rem",
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row.original.action ?? "N/A"}
            </div>
            <div
              title={row.original.description}
              style={{
                fontSize: "0.75rem",
                color: "#6b7280",
                marginTop: 2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row.original.description}
            </div>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "category",
    header: () => <div style={{ whiteSpace: "nowrap" }}>Category</div>,
    cell: ({ row }) => {
      const { bg, color } = getCategoryColor(row.original.category);
      return (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 2,
            py: 0.5,
            borderRadius: "999px",
            backgroundColor: bg,
            color,
            fontSize: "0.8rem",
            fontWeight: 500,
            whiteSpace: "nowrap",
          }}
        >
          {row.original.category ?? "N/A"}
        </Box>
      );
    },
  },
  {
    accessorKey: "sourceService",
    header: () => <div style={{ whiteSpace: "nowrap" }}>Source</div>,
    cell: ({ row }) => (
      <div style={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}>
        {row.original.sourceService ?? "N/A"}
      </div>
    ),
  },
  {
    accessorKey: "status",
    header: () => <div style={{ whiteSpace: "nowrap" }}>Status</div>,
    cell: ({ row }) => {
      const { bg, color } = getStatusColor(row.original.status);
      return (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 2,
            py: 0.5,
            borderRadius: "999px",
            backgroundColor: bg,
            color,
            fontSize: "0.8rem",
            fontWeight: 500,
            whiteSpace: "nowrap",
          }}
        >
          {formatLabel(row.original.status) ?? "N/A"}
        </Box>
      );
    },
  },
  {
    id: "actions",
    header: () => <div style={{ whiteSpace: "nowrap" }}>Action</div>,
    cell: ({ row }) => <ViewActivityCell activity={row.original} />,
  },
];
